import { Chip, styled, useTheme } from "@mui/material";

import { ProjectState } from "Infrastructure/Api/Api";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { isPropValid } from "Infrastructure/Utils/StyleUtils";

type StatusChipProps = {
  projectState: ProjectState;
};

type StatusConfig = {
  label: string;
  backgroundColor: string;
  textColor: string;
};

const StyledChip = styled(Chip, { shouldForwardProp: isPropValid })<{
  $backgroundColor: string;
  $textColor: string;
}>`
  background-color: ${(props) => props.$backgroundColor};
  color: ${(props) => props.$textColor};
  font-size: 12px;
  font-weight: 400;

  .MuiChip-label {
    padding: ${(props) => props.theme.spacing(0, Spacing.M)};
  }
`;

export const StatusChip = ({
  projectState: status,
}: StatusChipProps): React.ReactNode => {
  const { t } = useResource();
  const theme = useTheme();

  const getStatusConfig = (): StatusConfig => {
    switch (status) {
      case ProjectState.Done:
        return {
          label: t(Resources.Common.StatusDone),
          backgroundColor: theme.palette.success.dark,
          textColor: theme.palette.text.primary,
        };
      case ProjectState.Closed:
        return {
          label: t(Resources.Common.StatusClosed),
          backgroundColor: theme.palette.error.dark,
          textColor: theme.palette.text.primary,
        };
      case ProjectState.InProgress:
        return {
          label: t(Resources.Common.StatusInProgress),
          backgroundColor: theme.palette.info.dark,
          textColor: theme.palette.text.primary,
        };
      case ProjectState.Draft:
        return {
          label: t(Resources.Common.StatusDraft),
          backgroundColor: theme.palette.warning.dark,
          textColor: theme.palette.text.primary,
        };
      default:
        return {
          label: status,
          backgroundColor: theme.palette.error.dark,
          textColor: theme.palette.text.primary,
        };
    }
  };

  const config = getStatusConfig();

  return (
    <StyledChip
      label={config.label}
      $backgroundColor={config.backgroundColor}
      $textColor={config.textColor}
    />
  );
};
